import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import type { FilterProfile } from '@jobos/shared';

interface FilterProfileManagerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  profiles: FilterProfile[];
  activeProfileName: string;
  onSaveProfile: (name: string, makeDefault: boolean) => void;
  onUpdateProfile: (name: string) => void;
  onDeleteProfile: (id: string, name: string) => void;
  onSetDefaultProfile: (id: string, name: string) => void;
}

export function FilterProfileManagerModal({
  open,
  onOpenChange,
  profiles,
  activeProfileName,
  onSaveProfile,
  onUpdateProfile,
  onDeleteProfile,
  onSetDefaultProfile,
}: FilterProfileManagerModalProps) {
  const [newProfileName, setNewProfileName] = useState('');
  const [newProfileDefault, setNewProfileDefault] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);

  const sortedProfiles = useMemo(() => {
    return [...profiles].sort((a, b) => {
      const aDefault = a.isDefault;
      const bDefault = b.isDefault;
      if (aDefault !== bDefault) {
        return aDefault ? -1 : 1;
      }
      return a.name.localeCompare(b.name);
    });
  }, [profiles]);

  const handleSave = () => {
    const trimmedName = newProfileName.trim();
    if (!trimmedName) return;
    onSaveProfile(trimmedName, newProfileDefault);
    setNewProfileName('');
    setNewProfileDefault(false);
  };

  useEffect(() => {
    if (open) return;
    setPendingDelete(null);
    setNewProfileName('');
    setNewProfileDefault(false);
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Filter Profiles</DialogTitle>
          <DialogDescription>
            Save, set a default, or delete profiles for quick access.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <div className="space-y-3">
            <div className="text-sm font-medium text-gray-900">Saved Profiles</div>
            {sortedProfiles.length === 0 ? (
              <div className="text-sm text-gray-500">No profiles saved yet.</div>
            ) : (
              <div className="space-y-2">
                {sortedProfiles.map((profile) => {
                  const isDefault = profile.isDefault;
                  const isPendingDelete = pendingDelete === profile.name;
                  const isActive = profile.name === activeProfileName;
                  return (
                    <div
                      key={profile.name}
                      className={`flex items-center justify-between gap-3 rounded border px-3 py-2 ${
                        isActive ? 'border-blue-500 bg-blue-50' : ''
                      }`}
                    >
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-900">
                          {profile.name}
                        </span>
                        <div className="flex gap-2 text-xs text-gray-500">
                          {isDefault && <span>Default profile</span>}
                          {isActive && <span className="text-blue-600">• Active</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onUpdateProfile(profile.name)}
                        >
                          Update to Current
                        </Button>
                        {!isDefault && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onSetDefaultProfile(profile.id, profile.name)}
                          >
                            Set Default
                          </Button>
                        )}
                        {isPendingDelete ? (
                          <>
                            <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => {
                                onDeleteProfile(profile.id, profile.name);
                                setPendingDelete(null);
                              }}
                          >
                            Confirm
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setPendingDelete(null)}
                            >
                              Cancel
                            </Button>
                          </>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPendingDelete(profile.name)}
                          >
                            Delete
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="space-y-3">
            <div className="text-sm font-medium text-gray-900">Save New Profile</div>
            <div className="space-y-3">
              <Input
                placeholder="Profile name"
                value={newProfileName}
                onChange={(e) => setNewProfileName(e.target.value)}
              />
              <div className="flex items-center justify-between rounded border px-3 py-2">
                <div className="text-sm text-gray-700">Set as default</div>
                <Switch
                  checked={newProfileDefault}
                  onCheckedChange={(checked: boolean) => setNewProfileDefault(checked)}
                />
              </div>
              <div className="text-xs text-gray-500">
                Enter a name to save your current filters as a new profile. If the name matches an existing profile, it will be overwritten.
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button onClick={handleSave}>Save Profile</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
